import { useParams, Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import {
  Building,
  CheckCircle,
  ExternalLink,
  Package,
  Calendar,
} from 'lucide-react'
import { api, Publisher, PackageSummary } from '../utils/api'
import PackageCard from '../components/PackageCard'

export default function CreatorPage() {
  const { publisherId } = useParams<{ publisherId: string }>()
  const [publisher, setPublisher] = useState<Publisher | null>(null)
  const [packages, setPackages] = useState<PackageSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function loadCreator() {
      if (!publisherId) return

      setLoading(true)
      setError(null)
      console.log('[CreatorPage] Loading publisher:', publisherId)

      try {
        const [publisherData, packagesResponse] = await Promise.all([
          api.getPublisher(publisherId),
          api.getPublisherPackages(publisherId),
        ])
        setPublisher(publisherData)
        setPackages(packagesResponse.items)
      } catch (err) {
        console.error('[CreatorPage] Failed to load publisher:', err)
        setError('Publisher not found')
      } finally {
        setLoading(false)
      }
    }

    loadCreator()
  }, [publisherId])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="flex items-center space-x-4 mb-8">
              <div className="w-20 h-20 bg-gray-200 rounded-full" />
              <div className="flex-1">
                <div className="h-6 bg-gray-200 rounded w-1/3 mb-2" />
                <div className="h-4 bg-gray-200 rounded w-1/4" />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !publisher) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Building className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Publisher not found
          </h2>
          <p className="text-gray-500 mb-6">
            The publisher you're looking for doesn't exist.
          </p>
          <Link to="/browse" className="btn btn-primary">
            Browse all skills
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="mb-6 text-sm">
          <ol className="flex items-center space-x-2 text-gray-500">
            <li>
              <Link to="/browse" className="hover:text-gray-700">
                Browse
              </Link>
            </li>
            <li>/</li>
            <li className="text-gray-900">Publisher</li>
          </ol>
        </nav>

        {/* Header */}
        <div className="card p-8 mb-8">
          <div className="flex items-start space-x-6">
            <div className="w-20 h-20 bg-gradient-to-br from-primary-100 to-accent-100 rounded-full flex items-center justify-center">
              <Building className="w-10 h-10 text-primary-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-3">
                <h1 className="text-2xl font-bold text-gray-900">
                  {publisher.display_name}
                </h1>
                {publisher.verified && (
                  <span className="badge badge-verified">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Verified
                  </span>
                )}
              </div>
              <p className="text-gray-500 mt-1">@{publisher.slug}</p>

              <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center">
                  <Package className="w-4 h-4 mr-1" />
                  {publisher.package_count || packages.length} skills
                </div>
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  Joined{' '}
                  {new Date(publisher.created_at).toLocaleDateString('en-US', {
                    month: 'long',
                    year: 'numeric',
                  })}
                </div>
                {publisher.website_url && (
                  <a
                    href={publisher.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-primary-600 hover:text-primary-700"
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Website
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tools */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Skills by {publisher.display_name}
          </h2>

          {packages.length === 0 ? (
            <div className="card p-8 text-center">
              <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No skills published yet</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {packages.map((pkg) => (
                <PackageCard key={pkg.package_id} package={pkg} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
